"use client";

import * as React from "react";
import Image from "next/image";
import { Play } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface VideoPlayerProps extends React.HTMLAttributes<HTMLDivElement> {
  thumbnailUrl: string;
  videoUrl: string;
  title: string;
  description?: string;
  textHref?: string;
  thumbnailPriority?: boolean;
  aspectRatio?: "16/9" | "4/3" | "1/1";
  showTextOverlay?: boolean;
}

const aspectRatioClasses: Record<NonNullable<VideoPlayerProps["aspectRatio"]>, string> = {
  "16/9": "aspect-video",
  "4/3": "aspect-[4/3]",
  "1/1": "aspect-square",
};

const VideoPlayer = React.forwardRef<HTMLDivElement, VideoPlayerProps>(
  (
    {
      className,
      thumbnailUrl,
      videoUrl,
      title,
      description,
      textHref,
      thumbnailPriority = false,
      aspectRatio = "16/9",
      showTextOverlay = true,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <div
          ref={ref}
          className={cn(
            "relative h-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm",
            className
          )}
          {...props}
        >
          <div
            className={cn(
              "relative overflow-hidden",
              aspectRatioClasses[aspectRatio]
            )}
          >
            <Image
              alt={`Превью для ${title}`}
              fill
              src={thumbnailUrl}
              className="object-cover object-center"
              sizes="(max-width: 1024px) 100vw, 33vw"
              quality={60}
              priority={thumbnailPriority}
            />
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />

            {showTextOverlay && textHref ? (
              <a
                href={textHref}
                className="absolute inset-x-0 bottom-0 z-20 block rounded-b-2xl px-5 py-3 text-white transition-all duration-200 hover:bg-black/45 focus-visible:bg-black/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                aria-label={`Перейти на страницу: ${title}`}
              >
                <h3 className="text-lg font-semibold lg:text-2xl">{title}</h3>
                {description ? (
                  <p className="mt-1 max-w-lg text-sm text-white/85 lg:text-base">
                    {description}
                  </p>
                ) : null}
              </a>
            ) : showTextOverlay ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 rounded-b-2xl px-5 py-3 text-white">
                <h3 className="text-lg font-semibold lg:text-2xl">{title}</h3>
                {description ? (
                  <p className="mt-1 max-w-lg text-sm text-white/85 lg:text-base">
                    {description}
                  </p>
                ) : null}
              </div>
            ) : null}

            <DialogTrigger asChild>
              <button
                type="button"
                className="group absolute inset-0 z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label={`Открыть видео: ${title}`}
                suppressHydrationWarning
              >
                <span className="absolute inset-0" />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="-translate-y-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:bg-white/30 lg:-translate-y-8 lg:h-16 lg:w-16">
                    <Play className="h-6 w-6 fill-white text-white lg:h-8 lg:w-8" />
                  </span>
                </span>
              </button>
            </DialogTrigger>
          </div>
        </div>

        <DialogContent className="flex max-h-[95dvh] w-[95vw] max-w-5xl flex-col border-0 bg-black p-2 shadow-2xl sm:rounded-2xl [&>button]:bg-white/10 [&>button]:text-white [&>button]:opacity-100 [&>button]:ring-offset-black [&>button]:hover:bg-white/20">
          <DialogTitle className="sr-only">{title}</DialogTitle>
          {description ? (
            <DialogDescription className="sr-only">
              {description}
            </DialogDescription>
          ) : null}
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-black">
            {isOpen ? (
              <video
                key={videoUrl}
                src={videoUrl}
                className="max-h-[calc(95dvh-1rem)] w-auto max-w-full object-contain"
                controls
                autoPlay
                playsInline
                preload="metadata"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

VideoPlayer.displayName = "VideoPlayer";

export { VideoPlayer };
